import React, { useState, useEffect, useMemo } from 'react';
import { X, Send, Search, Users, MapPin, UserCircle, Plus, Trash2, Loader2, Check, AlertCircle, Briefcase, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getUsersPaginated, getEtlConfigForHotel, sendFileTrackingEmail } from '../../utils/api';

// Message templates based on file status
const MESSAGE_TEMPLATES = {
  missed: {
    label: 'Missed File',
    icon: 'XCircle',
    color: 'red',
    getSubject: (locationName, fileType, date) =>
      `Action Required: Missing ${fileType} Report - ${locationName} - ${date}`,
    getMessage: (locationName, fileType, date) =>
`The ${fileType} report for ${locationName} has not been received for the business date of ${date}.

Please submit this report as soon as possible by:
1. Replying to this email with the report attached
2. Or uploading directly to the Lodgeic file tracking dashboard

If you have any questions or need assistance, please let us know.`
  },
  failed: {
    label: 'Failed Processing',
    icon: 'AlertCircle',
    color: 'purple',
    getSubject: (locationName, fileType, date) =>
      `Processing Error: ${fileType} Report - ${locationName} - ${date}`,
    getMessage: (locationName, fileType, date) =>
`The ${fileType} report for ${locationName} (business date: ${date}) was received but encountered an error during processing.

This may be due to:
- Incorrect file format
- Missing required data fields
- File corruption

Please review and resubmit a corrected version of the report. If you need assistance understanding the error, please reach out.`
  },
  review: {
    label: 'Needs Review',
    icon: 'Clock',
    color: 'orange',
    getSubject: (locationName, fileType, date) =>
      `Review Required: ${fileType} Report - ${locationName} - ${date}`,
    getMessage: (locationName, fileType, date) =>
`The ${fileType} report for ${locationName} (business date: ${date}) requires manual review.

The file has been received and processed, but there may be a date mismatch or other issue that needs verification before the data can be finalized.

Please review the file tracking dashboard for details and clear the review flag once verified.`
  },
  general: {
    label: 'General Update',
    icon: 'FileText',
    color: 'blue',
    getSubject: (locationName, fileType, date) =>
      `File Tracking Update - ${date}`,
    getMessage: (locationName, fileType, date) =>
`This is an update regarding file tracking for ${date}.

Please review the status of the files listed below and take any necessary action.`
  }
};

// Role badge component
const RoleBadge = ({ role }) => {
  const roleConfig = {
    superAdmin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Super Admin' },
    orgAdmin: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Org Admin' },
    user: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'User' }
  };
  const config = roleConfig[role] || roleConfig.user;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Recipient chip component
const RecipientChip = ({ email, name, onRemove }) => (
  <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
    <span className="truncate max-w-[200px]" title={email}>
      {name || email}
    </span>
    <button
      onClick={() => onRemove(email)}
      className="p-0.5 hover:bg-blue-100 rounded-full transition-colors"
    >
      <X size={12} />
    </button>
  </div>
);

// User search result item
const UserSearchItem = ({ user, onSelect, isSelected, locations = [] }) => {
  // Find location names for this user
  const userLocationNames = useMemo(() => {
    return user.locations?.map(loc => {
      const location = locations.find(l => l.id === loc.id || l.locationId === loc.id);
      return location?.name || location?.locationName || `Location ${loc.id}`;
    }).slice(0, 3) || [];
  }, [user.locations, locations]);

  return (
    <button
      onClick={() => onSelect(user)}
      disabled={isSelected}
      className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-green-50' : ''}`}
    >
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
        <UserCircle size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-800 truncate">{user.fullName}</span>
          <RoleBadge role={user.role} />
          {isSelected && <Check size={14} className="text-green-600 flex-shrink-0" />}
        </div>
        <div className="text-xs text-gray-500 truncate">{user.email}</div>
        {user.jobTitle && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Briefcase size={10} />
            <span className="truncate">{user.jobTitle}</span>
          </div>
        )}
        {userLocationNames.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
            <MapPin size={10} />
            <span className="truncate">
              {userLocationNames.join(', ')}
              {user.locations?.length > 3 && ` +${user.locations.length - 3} more`}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

// Alert recipients section
const AlertRecipientsSection = ({ alertRecipients, selectedEmails, onSelectEmail }) => {
  if (!alertRecipients) return null;

  const allRecipients = [
    ...alertRecipients.initial.map(e => ({ email: e, type: 'Initial Alert' })),
    ...alertRecipients.secondary.map(e => ({ email: e, type: 'Escalation Alert' })),
    ...alertRecipients.failed.map(e => ({ email: e, type: 'Failed Processing Alert' }))
  ];

  // Remove duplicates
  const uniqueRecipients = allRecipients.filter(
    (r, i, arr) => arr.findIndex(a => a.email === r.email) === i
  );

  if (uniqueRecipients.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
        Configured Alert Recipients
      </div>
      <div className="flex flex-wrap gap-2">
        {uniqueRecipients.map(({ email, type }) => {
          const isSelected = selectedEmails.includes(email);
          return (
            <button
              key={email}
              onClick={() => onSelectEmail(email)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                isSelected
                  ? 'bg-orange-50 border-orange-300 text-orange-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300'
              }`}
              title={type}
            >
              {isSelected && <Check size={12} />}
              <span>{email}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FileTrackingEmailModal = ({
  isOpen,
  onClose,
  records = [],
  effectiveDate,
  locations = [],
  currentOrganization,
  singleFileMode = false
}) => {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedRecordIds, setSelectedRecordIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  const [availableJobTitles, setAvailableJobTitles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [alertRecipients, setAlertRecipients] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);

  // Initialize selected records when modal opens
  useEffect(() => {
    if (isOpen && records.length > 0) {
      setSelectedRecordIds(new Set(records.map(r => r.id)));
    }
  }, [isOpen, records]);

  // Get selected records
  const selectedRecords = useMemo(() => {
    return records.filter(r => selectedRecordIds.has(r.id));
  }, [records, selectedRecordIds]);

  // Toggle record selection
  const toggleRecordSelection = (recordId) => {
    setSelectedRecordIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  // Select all / deselect all
  const selectAllRecords = () => setSelectedRecordIds(new Set(records.map(r => r.id)));
  const deselectAllRecords = () => setSelectedRecordIds(new Set());

  // Format date for display
  const formattedDate = useMemo(() => {
    if (!effectiveDate) return '';
    return new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }, [effectiveDate]);

  // Determine dominant status for auto-selecting template
  const dominantStatus = useMemo(() => {
    if (!records.length) return 'general';
    const missedCount = records.filter(r => r.status === 'missed').length;
    const failedCount = records.filter(r => r.status === 'failed').length;
    const reviewCount = records.filter(r => r.manualReview?.required && !r.manualReview?.cleared).length;

    if (missedCount > 0 && missedCount >= failedCount && missedCount >= reviewCount) return 'missed';
    if (failedCount > 0 && failedCount >= missedCount && failedCount >= reviewCount) return 'failed';
    if (reviewCount > 0) return 'review';
    return 'general';
  }, [records]);

  // Get location name from first record
  const primaryLocationName = useMemo(() => {
    if (!records.length) return '';
    const firstRecord = records[0];
    return firstRecord.metadata?.locationName || `Hotel ${firstRecord.hotelId}`;
  }, [records]);

  // Get file type from first record
  const primaryFileType = useMemo(() => {
    if (!records.length) return 'Report';
    return records[0].fileType || 'Report';
  }, [records]);

  // Auto-select template and generate subject/message on open
  useEffect(() => {
    if (isOpen && effectiveDate && records.length > 0 && !selectedTemplate) {
      setSelectedTemplate(dominantStatus);
    }
  }, [isOpen, effectiveDate, records, dominantStatus, selectedTemplate]);

  // Update subject and message when template changes
  const handleTemplateChange = (templateKey) => {
    setSelectedTemplate(templateKey);
    const template = MESSAGE_TEMPLATES[templateKey];
    if (template) {
      setSubject(template.getSubject(primaryLocationName, primaryFileType, formattedDate));
      setMessage(template.getMessage(primaryLocationName, primaryFileType, formattedDate));
    }
  };

  // Generate default subject/message based on template when it's set
  useEffect(() => {
    if (selectedTemplate && effectiveDate && !subject) {
      const template = MESSAGE_TEMPLATES[selectedTemplate];
      if (template) {
        setSubject(template.getSubject(primaryLocationName, primaryFileType, formattedDate));
        setMessage(template.getMessage(primaryLocationName, primaryFileType, formattedDate));
      }
    }
  }, [selectedTemplate, effectiveDate, primaryLocationName, primaryFileType, formattedDate, subject]);

  // Store ETL config data for enriching records when sending
  const [etlConfigData, setEtlConfigData] = useState({});

  // Load alert recipients and ETL config data for the records
  useEffect(() => {
    const loadAlertRecipientsAndConfig = async () => {
      if (!records.length) return;

      // Load ETL config for each unique hotelId
      const uniqueHotelIds = [...new Set(records.map(r => r.hotelId))];
      const configMap = {};

      for (const hotelId of uniqueHotelIds) {
        try {
          const config = await getEtlConfigForHotel(hotelId);
          if (config && config.fileConfigs) {
            configMap[hotelId] = config;
          }
        } catch (error) {
          console.error(`Error loading ETL config for hotel ${hotelId}:`, error);
        }
      }

      setEtlConfigData(configMap);

      // Get the first record's hotelId and fileType for alert recipients
      const firstRecord = records[0];
      const hotelId = firstRecord.hotelId;
      const fileType = firstRecord.fileType;

      const config = configMap[hotelId];
      if (config && config.fileConfigs) {
        const fileConfig = config.fileConfigs.find(fc => fc.fileType === fileType);
        if (fileConfig?.tracking) {
          const tracking = fileConfig.tracking;
          const recipients = {
            initial: [],
            secondary: [],
            failed: []
          };

          // Get initial alert users
          if (tracking.initialAlertUser) {
            recipients.initial = Array.isArray(tracking.initialAlertUser)
              ? tracking.initialAlertUser
              : [tracking.initialAlertUser];
          }

          // Get secondary alert users
          if (tracking.secondaryAlertUser) {
            recipients.secondary = Array.isArray(tracking.secondaryAlertUser)
              ? tracking.secondaryAlertUser
              : [tracking.secondaryAlertUser];
          }

          // Get failed processing alert users
          if (tracking.failedProcessUser) {
            recipients.failed = Array.isArray(tracking.failedProcessUser)
              ? tracking.failedProcessUser
              : [tracking.failedProcessUser];
          }

          setAlertRecipients(recipients);

          // In single file mode, auto-add alert recipients based on status
          if (singleFileMode && isOpen) {
            const status = firstRecord.status;
            const needsReview = firstRecord.manualReview?.required && !firstRecord.manualReview?.cleared;

            // Determine which recipients to pre-populate based on status
            let emailsToAdd = [];
            if (status === 'missed') {
              emailsToAdd = [...recipients.initial, ...recipients.secondary];
            } else if (status === 'failed' || needsReview) {
              emailsToAdd = [...recipients.failed, ...recipients.initial];
            } else {
              emailsToAdd = recipients.initial;
            }

            // Remove duplicates and add as recipients
            const uniqueEmails = [...new Set(emailsToAdd)].filter(e => e);
            if (uniqueEmails.length > 0) {
              setRecipients(uniqueEmails.map(email => ({ email, name: null })));
            }
          }
        }
      }
    };

    if (isOpen) {
      loadAlertRecipientsAndConfig();
    }
  }, [isOpen, records, singleFileMode]);

  // Enrich records with ETL config data for email template
  const enrichRecordsWithConfig = (recordsToEnrich) => {
    return recordsToEnrich.map(record => {
      const config = etlConfigData[record.hotelId];
      if (!config || !config.fileConfigs) return record;

      const fileConfig = config.fileConfigs.find(fc => fc.fileType === record.fileType);
      if (!fileConfig) return record;

      return {
        ...record,
        // Include tracking config data for email template
        tracking: {
          ...record.tracking,
          expectedFileName: fileConfig.tracking?.expectedFileName || fileConfig.auditFile,
          lastReceived: record.tracking?.lastReceived || record.reception?.receivedAt,
          lastSuccessfullyProcessed: record.tracking?.lastSuccessfullyProcessed || record.processing?.completedAt
        },
        // Include email matching configuration
        emailIntegration: fileConfig.emailIntegration,
        emailMatchType: fileConfig.emailIntegration?.emailMatchType || fileConfig.emailMatchType || 'file',
        auditFile: fileConfig.auditFile
      };
    });
  };

  // Cache all users when search panel opens for filtering
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Load all users when search panel opens
  useEffect(() => {
    const loadAllUsers = async () => {
      if (!showUserSearch || usersLoaded) return;

      try {
        const response = await getUsersPaginated({
          search: '',
          limit: 500 // Fetch more users for filtering
        });
        setAllUsers(response.users || []);
        setUsersLoaded(true);

        // Extract unique job titles for filter dropdown
        const jobTitles = [...new Set((response.users || []).map(u => u.jobTitle).filter(jt => jt && jt.trim()))].sort();
        if (jobTitles.length > 0) {
          setAvailableJobTitles(jobTitles);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadAllUsers();
  }, [showUserSearch, usersLoaded]);

  // Reset when modal closes
  useEffect(() => {
    if (!showUserSearch) {
      setUsersLoaded(false);
      setAllUsers([]);
    }
  }, [showUserSearch]);

  // Filter users based on search and filters
  useEffect(() => {
    const filterUsers = () => {
      if (!showUserSearch) return;

      // Only filter if there's a query OR filters are applied
      const hasFilters = roleFilter || locationFilter || jobTitleFilter;
      if (!searchQuery && !hasFilters) {
        setSearchResults([]);
        return;
      }

      let users = [...allUsers];

      // Apply search query filter
      if (searchQuery) {
        const queryLower = searchQuery.toLowerCase();
        users = users.filter(user => {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
          const email = (user.email || '').toLowerCase();
          const jobTitle = (user.jobTitle || '').toLowerCase();
          return fullName.includes(queryLower) || email.includes(queryLower) || jobTitle.includes(queryLower);
        });
      }

      // Apply role filter
      if (roleFilter) {
        users = users.filter(user => {
          const userRole = user.isSuperAdmin === 1 ? 'superAdmin' :
                         user.isAdmin === 1 ? 'orgAdmin' : 'user';
          return userRole === roleFilter;
        });
      }

      // Apply job title filter (case-insensitive)
      if (jobTitleFilter) {
        const filterLower = jobTitleFilter.toLowerCase();
        users = users.filter(user =>
          user.jobTitle && user.jobTitle.toLowerCase() === filterLower
        );
      }

      // Apply location filter
      if (locationFilter) {
        const locIdNum = parseInt(locationFilter);
        const locIdStr = String(locationFilter);
        users = users.filter(user => {
          return Object.values(user.orgAccess || {}).some(org => {
            const locationIds = org.locationIds || [];
            // Check both number and string versions to handle type mismatches
            return locationIds.includes(locIdNum) || locationIds.includes(locIdStr) ||
                   locationIds.some(id => String(id) === locIdStr || parseInt(id) === locIdNum);
          });
        });
      }

      // Map users to expected format
      const mappedUsers = users.map(user => ({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        jobTitle: user.jobTitle || null,
        role: user.isSuperAdmin === 1 ? 'superAdmin' : user.isAdmin === 1 ? 'orgAdmin' : 'user',
        roleDisplay: user.isSuperAdmin === 1 ? 'Super Admin' : user.isAdmin === 1 ? 'Org Admin' : 'User',
        locations: Object.entries(user.orgAccess || {}).flatMap(([orgCode, org]) =>
          (org.locationIds || []).map(id => ({ id, orgCode }))
        ),
        orgAccess: user.orgAccess
      }));

      setSearchResults(mappedUsers);
      setSearching(false);
    };

    // Small delay for smoother UX
    const debounce = setTimeout(filterUsers, 100);
    return () => clearTimeout(debounce);
  }, [searchQuery, roleFilter, locationFilter, jobTitleFilter, showUserSearch, allUsers]);

  // Add recipient
  const addRecipient = (email, name = null) => {
    if (!recipients.some(r => r.email === email)) {
      setRecipients([...recipients, { email, name }]);
    }
  };

  // Remove recipient
  const removeRecipient = (email) => {
    setRecipients(recipients.filter(r => r.email !== email));
  };

  // Handle user selection from search
  const handleSelectUser = (user) => {
    addRecipient(user.email, user.fullName);
  };

  // Handle alert recipient selection
  const handleSelectAlertRecipient = (email) => {
    if (recipients.some(r => r.email === email)) {
      removeRecipient(email);
    } else {
      addRecipient(email);
    }
  };

  // Send email
  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    // In single file mode, use all records; otherwise use selected records
    const recordsToSend = singleFileMode ? records : selectedRecords;

    if (recordsToSend.length === 0) {
      toast.error('Please select at least one file to include');
      return;
    }

    // Enrich records with ETL config data for proper email formatting
    const enrichedRecords = enrichRecordsWithConfig(recordsToSend);

    setSending(true);
    try {
      await sendFileTrackingEmail({
        recipients: recipients.map(r => r.email),
        subject,
        message,
        records: enrichedRecords,
        effectiveDate
      });

      toast.success(`Email sent to ${recipients.length} recipient(s) about ${recordsToSend.length} file(s)`);
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Reset state on close
  useEffect(() => {
    if (!isOpen) {
      setRecipients([]);
      setSubject('');
      setMessage('');
      setSelectedTemplate('');
      setSelectedRecordIds(new Set());
      setSearchQuery('');
      setRoleFilter('');
      setLocationFilter('');
      setJobTitleFilter('');
      setSearchResults([]);
      setShowUserSearch(false);
      setEtlConfigData({});
      setAlertRecipients(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedEmails = recipients.map(r => r.email);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Send size={20} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {singleFileMode ? 'Send Email About File' : 'Send File Tracking Email'}
              </h2>
              <p className="cp-help">
                {singleFileMode && records.length > 0 ? (
                  <>
                    {records[0].metadata?.locationName || `Hotel ${records[0].hotelId}`} - {records[0].fileType}
                  </>
                ) : (
                  <>
                    {records.length} record{records.length !== 1 ? 's' : ''} for{' '}
                    {effectiveDate ? new Date(effectiveDate + 'T00:00:00').toLocaleDateString() : 'selected date'}
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients
            </label>

            {/* Alert Recipients */}
            <AlertRecipientsSection
              alertRecipients={alertRecipients}
              selectedEmails={selectedEmails}
              onSelectEmail={handleSelectAlertRecipient}
            />

            {/* Selected Recipients */}
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {recipients.map(r => (
                  <RecipientChip
                    key={r.email}
                    email={r.email}
                    name={r.name}
                    onRemove={removeRecipient}
                  />
                ))}
              </div>
            )}

            {/* Add User Button */}
            <button
              onClick={() => setShowUserSearch(!showUserSearch)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {showUserSearch ? <X size={16} /> : <Plus size={16} />}
              {showUserSearch ? 'Close Search' : 'Add User'}
            </button>

            {/* User Search Panel */}
            {showUserSearch && (
              <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                {/* Search and Filters */}
                <div className="p-3 bg-gray-50 border-b border-gray-200 space-y-2">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name, email, or job title..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Roles</option>
                      <option value="superAdmin">Super Admin</option>
                      <option value="orgAdmin">Org Admin</option>
                      <option value="user">User</option>
                    </select>
                    <select
                      value={jobTitleFilter}
                      onChange={(e) => setJobTitleFilter(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Job Titles</option>
                      {availableJobTitles.map(title => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                    </select>
                  </div>
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="col-span-3 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id || loc.locationId} value={loc.id || loc.locationId}>
                        {loc.name || loc.locationName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Results */}
                <div className="max-h-48 overflow-y-auto">
                  {!usersLoaded ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">Loading users...</span>
                    </div>
                  ) : searching ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="py-8 text-center text-gray-500 text-sm">
                      {searchQuery || roleFilter || locationFilter || jobTitleFilter
                        ? 'No users found matching your filters'
                        : 'Search by name or use the filters above'}
                    </div>
                  ) : (
                    searchResults.map(user => (
                      <UserSearchItem
                        key={user.id}
                        user={user}
                        onSelect={handleSelectUser}
                        isSelected={selectedEmails.includes(user.email)}
                        locations={locations}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Effective Date Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-orange-800">
              <FileText size={18} />
              <span className="font-semibold">Effective Date:</span>
              <span>{formattedDate}</span>
            </div>
          </div>

          {/* Message Template Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Template
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(MESSAGE_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => handleTemplateChange(key)}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                    selectedTemplate === key
                      ? `bg-${template.color}-50 border-${template.color}-300 text-${template.color}-700`
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                  style={selectedTemplate === key ? {
                    backgroundColor: template.color === 'red' ? '#fef2f2' :
                      template.color === 'purple' ? '#faf5ff' :
                      template.color === 'orange' ? '#fff7ed' : '#eff6ff',
                    borderColor: template.color === 'red' ? '#fca5a5' :
                      template.color === 'purple' ? '#c4b5fd' :
                      template.color === 'orange' ? '#fdba74' : '#93c5fd',
                    color: template.color === 'red' ? '#b91c1c' :
                      template.color === 'purple' ? '#7c3aed' :
                      template.color === 'orange' ? '#c2410c' : '#1d4ed8'
                  } : {}}
                >
                  {template.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Select a template to pre-fill the subject and message. You can edit the content after selecting.
            </p>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Email subject..."
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Add a personal message..."
            />
          </div>

          {/* Records Selection - Show differently for single file mode */}
          {records.length > 0 && singleFileMode ? (
            // Single file mode - just show file info
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Included
              </label>
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                {(() => {
                  const record = records[0];
                  const needsReview = record.manualReview?.required && !record.manualReview?.cleared;
                  return (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800 font-medium">
                          {record.metadata?.locationName || `Hotel ${record.hotelId}`}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500">{record.fileType}</span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        needsReview
                          ? 'bg-orange-100 text-orange-700'
                          : record.status === 'missed'
                          ? 'bg-red-100 text-red-700'
                          : record.status === 'failed'
                          ? 'bg-purple-100 text-purple-700'
                          : record.status === 'processed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {needsReview ? 'Needs Review' : record.status}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : records.length > 0 && (
            // Multi-file mode - show checkboxes
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Files to Include ({selectedRecordIds.size} of {records.length})
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllRecords}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={deselectAllRecords}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  {records.map((record, idx) => {
                    const needsReview = record.manualReview?.required && !record.manualReview?.cleared;
                    const isSelected = selectedRecordIds.has(record.id);
                    return (
                      <label
                        key={record.id || idx}
                        className={`flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRecordSelection(record.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-800 font-medium">
                              {record.metadata?.locationName || `Hotel ${record.hotelId}`}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{record.fileType}</span>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            needsReview
                              ? 'bg-orange-100 text-orange-700'
                              : record.status === 'missed'
                              ? 'bg-red-100 text-red-700'
                              : record.status === 'failed'
                              ? 'bg-purple-100 text-purple-700'
                              : record.status === 'processed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {needsReview ? 'Needs Review' : record.status}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || recipients.length === 0 || !subject.trim() || (!singleFileMode && selectedRecords.length === 0)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileTrackingEmailModal;
